import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";
import { Zap } from "./useSearchZaps";
import { OrganizationZaps } from "./useQueryOrganizationZaps";

interface Input {
  id?: string;
  folderId?: string; // used for cache invalidation
}

export function useRenameZap({ id, folderId }: Input) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();

  const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
  const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
  const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");

  const mutation = trpc.zap.rename.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ title }) => {
      if (!id) {
        return;
      }

      // Update Zap under storageZaps key (search results view)
      queryClient.cancelQueries({ queryKey: storageKey });
      queryClient.setQueriesData(
        {
          queryKey: storageKey,
          type: "active",
        },
        (old: Record<string, Zap> | undefined) => {
          if (old?.[id]) {
            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              [id]: {
                ...old[id],
                title,
              },
            };
          }
          return old;
        },
      );

      // Update Zap under organizationZaps key (single folder view)
      queryClient.cancelQueries({ queryKey: orgKey });
      queryClient.setQueriesData(
        {
          queryKey: orgKey,
          type: "active",
        },
        (old: OrganizationZaps | undefined) => {
          if (old?.results?.find((zap) => zap.id === id)) {
            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              results: old.results.map((zap) => {
                if (zap.id === id) {
                  return {
                    ...zap,
                    title,
                  };
                }
                return zap;
              }),
            };
          }
          return old;
        },
      );
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: (zap) => {
      const zapId = (zap as any)?.zapId || zap?.id || "";

      // Invalidate storageZaps key to refetch data
      queryClient.invalidateQueries({ queryKey: storageKey });
      // Invalidate organizationZaps key to refetch data
      queryClient.invalidateQueries({ queryKey: orgKey });
      // Invalidate search Zaps to get most up to date last_modified data
      queryClient.invalidateQueries({ queryKey: searchKey });
      // Invalidate MPF Zaps
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({
          folderId,
        });
      }
      // Refetch project Zap if it exists
      if (zapId) {
        const projectKey = getQueryKey(trpc.zap.get, { id: zapId }, "any");
        const queries = queryClient.getQueriesData({ queryKey: projectKey });
        if (queries.length > 0) {
          queryClient.refetchQueries({ queryKey: projectKey });
        }
      }
    },
  });

  return {
    rename: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
